import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { BrandM, LabelM } from '@sorare/core/src/atoms/typography';
import { useEventsContext } from '@sorare/core/src/contexts/events';

const messages = defineMessages({
  videoDescription: {
    id: 'Baseball.Home.HowToPlay.Video.Description',
    defaultMessage: 'Everything you need to know.',
  },
  learnMoreCta: {
    id: 'Baseball.Home.HowToPlay.Cta.LearnMore',
    defaultMessage: 'Learn More',
  },
  playVideoCta: {
    id: 'Baseball.Home.HowToPlay.Cta.PlayVideo',
    defaultMessage: 'Play Video',
  },
  cardsHeadline: {
    id: 'Baseball.Home.HowToPlay.Cards.Headline',
    defaultMessage: 'Cards',
  },
  cardsDescription: {
    id: 'Baseball.Home.HowToPlay.Cards.Description',
    defaultMessage:
      'There are five types of cards, each with different scarcity values that unlock the ability to participate in different tournaments.',
  },
  rosterHeadline: {
    id: 'Baseball.Home.HowToPlay.Roster.Headline',
    defaultMessage: 'Roster',
  },
  rosterDescription: {
    id: 'Baseball.Home.HowToPlay.Roster.Description',
    defaultMessage:
      'A Sorare: MLB lineup consists of seven cards, based on player position. Players’ positions are indicated on their card.',
  },
  scoringHeadline: {
    id: 'Baseball.Home.HowToPlay.Scoring.Headline',
    defaultMessage: 'Scoring',
  },
  scoringDescription: {
    id: 'Baseball.Home.HowToPlay.Scoring.Description',
    defaultMessage:
      'The better your players perform in real-life, the higher you’ll finish on the leaderboard.',
  },
  gwHeadline: {
    id: 'Baseball.Home.HowToPlay.Gw.Headline',
    defaultMessage: 'Game Weeks',
  },
  gwDescription: {
    id: 'Baseball.Home.HowToPlay.Gw.Description',
    defaultMessage:
      'There are two competition windows within a week. Monday to Thursday and Friday to Sunday.',
  },
});

const links = {
  video: 'https://frontend-assets.sorare.com/videos/mlb_landing_tutorial.mp4',
  cards: 'https://mlbguide.sorare.com/sorare-mlb-cards/types-of-cards',
  roster: 'https://mlbguide.sorare.com/how-to-play/the-basics/roster',
  scoring: 'https://mlbguide.sorare.com/how-to-play/the-basics/scoring-system',
  gw: 'https://mlbguide.sorare.com/how-to-play/the-basics/game-weeks',
};

const Tile = styled.a`
  position: relative;
  display: flex;
  width: 100%;
  border-radius: var(--double-unit);
  flex-direction: column;
  gap: var(--unit);
  justify-content: flex-end;
  padding: var(--quadruple-unit) var(--triple-unit);
  text-align: left;
  color: var(--c-white);
  &:visited {
    color: var(--c-white);
  }
  &:hover,
  &:focus {
    color: var(--c-white);
    span {
      text-decoration: underline;
    }
  }

  background-color: var(--c-nd-50-opaque);
  background-position: center;
  background-size: cover;
  aspect-ratio: 1;
`;

const LearnMoreCta = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: inherit;
  font-size: var(--t-14);
  font-weight: 700;
  gap: var(--unit);
`;

const SharedTrackingParams = {
  sport: Sport.BASEBALL,
  interaction_context: 'homepage',
};

export const HowToPlayCardsTile = () => {
  const { track } = useEventsContext();

  return (
    <Tile
      style={{
        backgroundImage: `url(
          'https://frontend-assets.sorare.com/baseball/how-to-play/cards-tile.png'
        )`,
      }}
      aria-label={messages.cardsHeadline.defaultMessage}
      href={links.cards}
      onClick={() => {
        track('Click How To Unit', {
          unit_type: 'card guide',
          unit_destination: links.cards,
          ...SharedTrackingParams,
        });
      }}
    >
      <BrandM>
        <FormattedMessage {...messages.cardsHeadline} />
      </BrandM>
      <LabelM>
        <FormattedMessage {...messages.cardsDescription} />
      </LabelM>
      <LearnMoreCta>
        <FormattedMessage {...messages.learnMoreCta} />
        <FontAwesomeIcon icon={faArrowRight} />
      </LearnMoreCta>
    </Tile>
  );
};

export const HowToPlayRosterTile = () => {
  const { track } = useEventsContext();

  return (
    <Tile
      style={{
        backgroundImage: `url(
          'https://frontend-assets.sorare.com/baseball/how-to-play/roster-tile.png'
        )`,
      }}
      aria-label={messages.rosterHeadline.defaultMessage}
      href={links.roster}
      onClick={() => {
        track('Click How To Unit', {
          unit_type: 'roster guide',
          unit_destination: links.roster,
          ...SharedTrackingParams,
        });
      }}
    >
      <BrandM>
        <FormattedMessage {...messages.rosterHeadline} />
      </BrandM>
      <LabelM>
        <FormattedMessage {...messages.rosterDescription} />
      </LabelM>
      <LearnMoreCta>
        <FormattedMessage {...messages.learnMoreCta} />
        <FontAwesomeIcon icon={faArrowRight} />
      </LearnMoreCta>
    </Tile>
  );
};

export const HowToPlayScoringTile = () => {
  const { track } = useEventsContext();

  return (
    <Tile
      style={{
        backgroundImage: `url(
              'https://frontend-assets.sorare.com/baseball/how-to-play/scoring-tile.png'
            )`,
      }}
      aria-label={messages.scoringHeadline.defaultMessage}
      href={links.scoring}
      onClick={() => {
        track('Click How To Unit', {
          unit_type: 'scoring guide',
          unit_destination: links.scoring,
          ...SharedTrackingParams,
        });
      }}
    >
      <BrandM>
        <FormattedMessage {...messages.scoringHeadline} />
      </BrandM>
      <LabelM>
        <FormattedMessage {...messages.scoringDescription} />
      </LabelM>
      <LearnMoreCta>
        <FormattedMessage {...messages.learnMoreCta} />
        <FontAwesomeIcon icon={faArrowRight} />
      </LearnMoreCta>
    </Tile>
  );
};

export const HowToPlayGWTile = () => {
  const { track } = useEventsContext();

  return (
    <Tile
      style={{
        backgroundImage: `url(
              'https://frontend-assets.sorare.com/baseball/how-to-play/game-weeks-tile.png'
            )`,
      }}
      aria-label={messages.gwHeadline.defaultMessage}
      href={links.gw}
      onClick={() => {
        track('Click How To Unit', {
          unit_type: 'GW guide',
          unit_destination: links.gw,
          ...SharedTrackingParams,
        });
      }}
    >
      <BrandM>
        <FormattedMessage {...messages.gwHeadline} />
      </BrandM>
      <LabelM>
        <FormattedMessage {...messages.gwDescription} />
      </LabelM>
      <LearnMoreCta>
        <FormattedMessage {...messages.learnMoreCta} />
        <FontAwesomeIcon icon={faArrowRight} />
      </LearnMoreCta>
    </Tile>
  );
};
